import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useOrder } from '../contexts/OrderContext';
import { useUser } from '../contexts/UserContext';
import PaymentForm from '../components/PaymentForm';
import Spinner from '../components/Spinner';
import ErrorDisplay from '../components/ErrorDisplay';
import { getDaysUntilRefill, isUnpaidOrder, isUnderProviderReview } from '../utilities/refillRules';
import { api } from '../utilities/api';
import { findProductByCode, formatDispenseQuantity } from '../utilities/productDisplay';

const doseRequestLabels = {
  same: 'Stay on the same dose',
  increase: 'Increase to the next dose',
  decrease: 'Decrease to the previous dose',
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 'TBD';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (value) => {
  if (!value) return 'Not available';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleDateString();
};

function RefillsPage() {
  const navigate = useNavigate();
  const { user, loadingSession } = useUser();
  const orderContext = useOrder();
  // Refill buttons (Orderstable/OrdersPreview/Dashboard) always navigate here
  // via navigateWithContext(orderId, '/refills', 'order'), which sets
  // currentOrder to the SPECIFIC order that was clicked — a patient with
  // concurrent orders for two products must refill from that order's own
  // product, not from whichever order happens to be globally most recent.
  const navigatedOrder = orderContext?.currentOrder?.order_id ? orderContext.currentOrder : null;
  const lastOrder = navigatedOrder || orderContext?.latestOrder || null;

  const [currentStep, setCurrentStep] = useState('request');
  const [newOrderDetails, setNewOrderDetails] = useState(null);
  const [receiptData, setReceiptData] = useState({ pdfUrl: null, pdfFile: null });
  const [showPaymentReceipt, setShowPaymentReceipt] = useState(false);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasRequestedRefillData, setHasRequestedRefillData] = useState(false);
  const [hasSettledRefillData, setHasSettledRefillData] = useState(false);
  // Dose/price/signatura options for a refill must come from the full,
  // authenticated product list (OrderContext.products, backed by
  // remote_get_pricing_info) — not OrderContext.pricingData, which is the
  // public list (remote_get_public_pricing_info, is_public=1 only). A
  // patient can be refilling a product that isn't publicly advertised, and
  // only the authenticated list carries weight_tracking, route, and the
  // dispensing facets.
  const refillProducts = useMemo(() => orderContext?.products || [], [orderContext?.products]);
  const isRefillProductsLoading = Boolean(orderContext?.isProductsLoading);
  const [formData, setFormData] = useState({
    weight: '',
    doseRequest: 'same',
    order_qty: '1',
    abdominal_pain: 'no',
    abdominal_pain_details: '',
    gastrointestinal: 'no',
    gastrointestinal_details: '',
    other_side_effects: 'no',
    other_side_effects_details: '',
    complaint: '',
  });

  const pricingRows = useMemo(() => {
    if (!lastOrder?.product_name) return [];
    const productName = String(lastOrder.product_name).toLowerCase();
    return (refillProducts || [])
      .filter(item => String(item.product_name || '').toLowerCase() === productName)
      .sort((a, b) => String(a.product_code || '').localeCompare(String(b.product_code || ''), undefined, { numeric: true }));
  }, [lastOrder?.product_name, refillProducts]);

  const currentDoseIndex = useMemo(() => {
    if (!lastOrder?.product_code) return -1;
    return pricingRows.findIndex(item => String(item.product_code) === String(lastOrder.product_code));
  }, [lastOrder?.product_code, pricingRows]);

  const doseOptions = useMemo(() => {
    const current = currentDoseIndex >= 0 ? pricingRows[currentDoseIndex] : null;
    const previous = currentDoseIndex > 0 ? pricingRows[currentDoseIndex - 1] : null;
    const next = currentDoseIndex >= 0 && currentDoseIndex < pricingRows.length - 1 ? pricingRows[currentDoseIndex + 1] : null;
    return { same: current, decrease: previous, increase: next };
  }, [currentDoseIndex, pricingRows]);

  const selectedDose = doseOptions[formData.doseRequest] || doseOptions.same;
  const selectedQuantity = Number(formData.order_qty);
  const estimatedPrice = selectedDose && Number.isFinite(selectedQuantity)
    ? Number(selectedDose.price) * selectedQuantity
    : null;

  // Weight/BMI questions only apply to products flagged weight_tracking.
  // Look the current order's product up in the full product list by
  // product_code (most precise key); default to on (today's behavior) if
  // the flag is absent, e.g. during the pre-migration window.
  const currentProduct = useMemo(
    () => findProductByCode(refillProducts, lastOrder?.product_code),
    [lastOrder?.product_code, refillProducts]
  );
  const weightTrackingEnabled = currentProduct?.weight_tracking == null
    ? true
    : Number(currentProduct.weight_tracking) === 1;
  const latestWeightInfo = useMemo(() => {
    const historyWeight = orderContext?.orderHistory?.[0]?.weight;
    if (historyWeight) {
      return { value: historyWeight, source: 'latest recorded program weight' };
    }
    if (user?.weight) {
      return { value: user.weight, source: 'profile weight' };
    }
    return { value: '', source: '' };
  }, [orderContext?.orderHistory, user?.weight]);
  const profileDataNotes = useMemo(() => {
    const notes = [];
    if (!user?.height) notes.push('height');
    if (!user?.goal_weight) notes.push('goal weight');
    return notes;
  }, [user?.goal_weight, user?.height]);
  const isRefillDataLoading = Boolean(user?.userid) && !lastOrder && (
    loadingSession
    || orderContext?.isLoading
    || !hasSettledRefillData
  );

  const unavailableReason = useMemo(() => {
    if (isRefillDataLoading) return 'Medication history is loading.';
    if (!lastOrder) return 'No previous qualifying medication order was found.';
    if (isUnpaidOrder(lastOrder)) return 'You have an unpaid order. Pay the existing order before requesting another refill.';
    if (isRefillProductsLoading) return 'Medication pricing is loading.';
    if (!pricingRows.length) return 'Medication pricing is not available right now. Please try again later.';
    const daysLeft = getDaysUntilRefill(lastOrder);
    if (daysLeft > 0) return `This refill is available in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`;
    if (isUnderProviderReview(lastOrder)) {
      return 'Your previous order is still under provider review.';
    }
    return '';
  }, [isRefillDataLoading, lastOrder, isRefillProductsLoading, pricingRows.length]);

  useEffect(() => {
    setHasRequestedRefillData(false);
    setHasSettledRefillData(false);
  }, [user?.userid]);

  useEffect(() => {
    if (!user?.userid || hasRequestedRefillData) return;

    const refreshes = [];
    if (!lastOrder && !orderContext?.isOrdersLoading) {
      refreshes.push(orderContext.refreshOrders());
    }
    if (!orderContext?.orderHistory?.length && !orderContext?.isOrderHistoryLoading) {
      refreshes.push(orderContext.refreshOrderHistory());
    }
    if (!refillProducts.length && !isRefillProductsLoading) {
      refreshes.push(orderContext.refreshProducts());
    }

    if (refreshes.length > 0) {
      setHasRequestedRefillData(true);
      Promise.allSettled(refreshes).finally(() => setHasSettledRefillData(true));
      return;
    }

    if (
      !loadingSession
      && !orderContext?.isOrdersLoading
      && !orderContext?.isOrderHistoryLoading
      && !isRefillProductsLoading
    ) {
      setHasRequestedRefillData(true);
      setHasSettledRefillData(true);
    }
  }, [
    hasRequestedRefillData,
    lastOrder,
    orderContext,
    orderContext?.isOrderHistoryLoading,
    orderContext?.isOrdersLoading,
    orderContext?.orderHistory?.length,
    refillProducts.length,
    isRefillProductsLoading,
    loadingSession,
    user?.userid,
  ]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      weight: prev.weight || latestWeightInfo.value,
    }));
  }, [latestWeightInfo.value]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateRequest = () => {
    if (unavailableReason) return unavailableReason;
    if (weightTrackingEnabled) {
      const currentWeight = Number(formData.weight);
      if (!formData.weight) return 'Please enter your current weight.';
      if (!Number.isFinite(currentWeight) || currentWeight <= 0) return 'Please enter a valid current weight.';
      if (currentWeight < 70 || currentWeight > 700) return 'Please enter a current weight between 70 and 700 lbs.';
    }
    if (!selectedDose?.product_code) return 'Please choose a dose request that is available for your last medication.';
    if (!formData.order_qty) return 'Please choose a refill duration.';
    if (formData.abdominal_pain === 'yes' && !formData.abdominal_pain_details.trim()) {
      return 'Please describe the abdominal pain you experienced.';
    }
    if (formData.gastrointestinal === 'yes' && !formData.gastrointestinal_details.trim()) {
      return 'Please describe the nausea, vomiting, or diarrhea you experienced.';
    }
    if (formData.other_side_effects === 'yes' && !formData.other_side_effects_details.trim()) {
      return 'Please describe the other side effects you experienced.';
    }
    return '';
  };

  const handleReview = (event) => {
    event.preventDefault();
    const validationError = validateRequest();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setCurrentStep('review');
  };

  const buildComplaint = () => {
    const lines = [
      `Dose request: ${doseRequestLabels[formData.doseRequest]}`,
      `Abdominal pain: ${formData.abdominal_pain}`,
      formData.abdominal_pain === 'yes' ? `Abdominal pain details: ${formData.abdominal_pain_details}` : null,
      `Nausea/vomiting/diarrhea: ${formData.gastrointestinal}`,
      formData.gastrointestinal === 'yes' ? `GI details: ${formData.gastrointestinal_details}` : null,
      `Other side effects: ${formData.other_side_effects}`,
      formData.other_side_effects === 'yes' ? `Other side effect details: ${formData.other_side_effects_details}` : null,
      formData.complaint ? `Additional notes: ${formData.complaint}` : 'Additional notes: None',
    ];
    return lines.filter(Boolean).join('\n');
  };

  const handleSubmitRefill = async () => {
    if (isProcessing) return;

    const validationError = validateRequest();
    if (validationError) {
      setError(validationError);
      setCurrentStep('request');
      return;
    }

    setIsProcessing(true);
    setError(null);
    const intakeData = {
      drug: String(lastOrder.product_name || '').toLowerCase(),
      dosage: selectedDose.dosage || lastOrder.dosage,
      product_code: selectedDose.product_code,
      order_qty: formData.order_qty,
      height: user?.height || '',
      weight: weightTrackingEnabled ? formData.weight : '',
      desiredWeight: user?.goal_weight || '',
      dose_request: formData.doseRequest,
      abdominal_pain: formData.abdominal_pain,
      abdominal_pain_details: formData.abdominal_pain_details,
      gastrointestinal: formData.gastrointestinal,
      gastrointestinal_details: formData.gastrointestinal_details,
      other_side_effects: formData.other_side_effects,
      other_side_effects_details: formData.other_side_effects_details,
      complaint: buildComplaint(),
    };

    const response = await orderContext.submitIntake(intakeData);
    setIsProcessing(false);

    if (response.success) {
      // remote_add_order's order carries no provider or quote. Payment needs
      // what remote_get_order returns, so fetch it before the payment step.
      // If that fails, payment shows "cannot show the amount" (fail safe).
      const detail = await api.getOrderDetail({ orderid: response.order?.order_id });
      const payableOrder = detail?.success && detail.data ? detail.data : response.order;
      setNewOrderDetails(payableOrder);
      const orderLoaded = orderContext.setOrder(payableOrder?.order_id, payableOrder);
      if (!orderLoaded) {
        orderContext.setCurrentOrder(payableOrder);
      }
      toast.success('Refill order created. Continue to payment.');
      setCurrentStep('payment');
      return;
    }

    setError(response.error || 'Failed to create refill order.');
  };

  const handlePaymentSuccess = async (paymentData) => {
    setError(null);
    // FX-179: the backend captured AND recorded this payment inside
    // /cp/orders/remote_capture_paypal_order. Nothing is applied here — this
    // only refreshes our view of the orders.
    await orderContext.completeRecordedPayment();
    toast.success('Payment applied.');
    if (paymentData?.url || paymentData?.pdfFile) {
      setReceiptData({ pdfUrl: paymentData.url || null, pdfFile: paymentData.pdfFile || null });
      setShowPaymentReceipt(true);
    }
  };

  const handlePaymentError = (error) => {
    // A cancelled payment never reached PayPal, so pointing the patient back
    // at the order is safe and useful.
    if (error?.type === 'cancelled') {
      setError(`Payment was not completed. Your refill order ${newOrderDetails?.order_id || ''} was created and can be paid from the dashboard.`);
      return;
    }

    // Anything else: the backend is the only party that knows whether money
    // moved, and its message has already been shown verbatim. This page must
    // NOT add "can be paid from the dashboard" on top of a possible "do not
    // pay again" - that sentence is an invitation to a second charge.
    setError(null);
  };

  const lastOrderQuantity = formatDispenseQuantity(currentProduct, lastOrder?.order_qty);

  const renderLastOrderSummary = () => (
    <section className="w-full border border-gray-200 rounded bg-white p-4">
      <h4 className="font-semibold text-primary mb-3">Last Order</h4>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 text-sm">
        <p><span className="font-semibold text-gray-600">Medication:</span> {lastOrder?.product_name || 'Not available'}</p>
        <p><span className="font-semibold text-gray-600">Route:</span> {currentProduct?.route || 'Not available'}</p>
        <p><span className="font-semibold text-gray-600">Dose:</span> {lastOrder?.dosage || 'Not available'}</p>
        <p>
          <span className="font-semibold text-gray-600">Quantity:</span>{' '}
          {lastOrderQuantity || `${lastOrder?.order_qty || 'Not available'} month${String(lastOrder?.order_qty) === '1' ? '' : 's'}`}
          {lastOrderQuantity && ` (${lastOrder?.order_qty} month${String(lastOrder?.order_qty) === '1' ? '' : 's'})`}
        </p>
        <p><span className="font-semibold text-gray-600">Ordered:</span> {formatDate(lastOrder?.order_date)}</p>
        <p><span className="font-semibold text-gray-600">Eligibility:</span> {unavailableReason || 'Available now'}</p>
      </div>
    </section>
  );

  const renderYesNo = (name, label, detailName, detailLabel) => (
    <div className="border border-gray-200 rounded p-3 bg-white">
      <label className="block text-primary font-medium mb-2">{label}</label>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm text-neutral-dark">
          <input type="radio" name={name} value="no" checked={formData[name] === 'no'} onChange={updateField} />
          No
        </label>
        <label className="flex items-center gap-2 text-sm text-neutral-dark">
          <input type="radio" name={name} value="yes" checked={formData[name] === 'yes'} onChange={updateField} />
          Yes
        </label>
      </div>
      {formData[name] === 'yes' && (
        <textarea
          name={detailName}
          value={formData[detailName]}
          onChange={updateField}
          rows="3"
          className="mt-3 w-full p-2 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder={detailLabel}
        />
      )}
    </div>
  );

  const renderRequest = () => (
    <form onSubmit={handleReview} className="space-y-4">
      {renderLastOrderSummary()}

      {unavailableReason && (
        <div className="border border-yellow-200 bg-yellow-50 text-yellow-900 rounded p-4">
          {unavailableReason}
        </div>
      )}

      <section className="w-full border border-gray-200 rounded bg-white p-4 space-y-4">
        <h4 className="font-semibold text-primary">Refill Request</h4>
        <div className={`grid grid-cols-1 gap-4 ${weightTrackingEnabled ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
          {weightTrackingEnabled && (
            <div>
              <label htmlFor="weight" className="block text-primary mb-1">Current Weight (lbs) *</label>
              <input
                id="weight"
                name="weight"
                type="number"
                step="0.1"
                value={formData.weight}
                onChange={updateField}
                className="w-full p-2 border border-neutral rounded text-right focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={Boolean(unavailableReason)}
                required
              />
              {latestWeightInfo.source && (
                <p className="mt-1 text-xs text-gray-500">
                  Pre-filled from your {latestWeightInfo.source}. Update it if your current weight has changed.
                </p>
              )}
            </div>
          )}
          <div>
            <label htmlFor="order_qty" className="block text-primary mb-1">Duration *</label>
            <select
              id="order_qty"
              name="order_qty"
              value={formData.order_qty}
              onChange={updateField}
              className="w-full p-2 border border-neutral rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={Boolean(unavailableReason)}
              required
            >
              <option value="1">1 month</option>
              <option value="3">3 months</option>
              <option value="6">6 months</option>
            </select>
          </div>
          <div>
            <label className="block text-primary mb-1">Quantity</label>
            <div className="p-2 border border-neutral rounded bg-gray-100 text-right text-neutral-dark">
              {formatDispenseQuantity(selectedDose, formData.order_qty) || 'Not available'}
            </div>
          </div>
          <div>
            <label className="block text-primary mb-1">Estimated Price</label>
            <div className="p-2 border border-neutral rounded bg-gray-100 text-right font-semibold text-secondary-dark">
              {formatCurrency(estimatedPrice)}
            </div>
          </div>
        </div>

        <div>
          <p className="block text-primary mb-2">Dose Request *</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(doseRequestLabels).map(([value, label]) => {
              const dose = doseOptions[value];
              const disabled = Boolean(unavailableReason) || !dose;
              return (
                <label key={value} className={`border rounded p-3 ${disabled ? 'bg-gray-100 text-gray-500' : 'bg-white text-neutral-dark'}`}>
                  <span className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="doseRequest"
                      value={value}
                      checked={formData.doseRequest === value}
                      onChange={updateField}
                      disabled={disabled}
                      className="mt-1"
                    />
                    <span>
                      <span className="block font-medium">{label}</span>
                      <span className="block text-sm">{dose?.dosage || 'Not available for this medication'}</span>
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      </section>

      {profileDataNotes.length > 0 && (
        <div className="border border-blue-200 bg-blue-50 text-blue-900 rounded p-4 text-sm">
          Your profile is missing {profileDataNotes.join(', ')}. The refill can still be reviewed, but updating your profile helps keep provider review and future forms accurate.
        </div>
      )}

      <section className="w-full border border-gray-200 rounded bg-white p-4 space-y-3">
        <h4 className="font-semibold text-primary">Side Effects</h4>
        {renderYesNo('abdominal_pain', 'Have you experienced moderate to severe abdominal pain?', 'abdominal_pain_details', 'Describe when it started, severity, and whether it is ongoing.')}
        {renderYesNo('gastrointestinal', 'Have you experienced moderate to severe nausea, vomiting, or diarrhea?', 'gastrointestinal_details', 'Describe symptoms, severity, and how long they lasted.')}
        {renderYesNo('other_side_effects', 'Any other side effects or concerns?', 'other_side_effects_details', 'Describe your symptoms or concerns.')}
        <div>
          <label htmlFor="complaint" className="block text-primary mb-1">Additional Notes</label>
          <textarea
            id="complaint"
            name="complaint"
            value={formData.complaint}
            onChange={updateField}
            rows="4"
            className="w-full p-2 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Anything else you want your provider to know."
            disabled={Boolean(unavailableReason)}
          />
        </div>
      </section>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => navigate('/dashboard')} className="px-4 py-3 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
          Cancel
        </button>
        <button type="submit" className="px-4 py-3 bg-button text-white rounded hover:bg-button-light disabled:opacity-50" disabled={Boolean(unavailableReason)}>
          Review Refill Request
        </button>
      </div>
    </form>
  );

  const renderReview = () => (
    <div className="space-y-4">
      {renderLastOrderSummary()}
      <section className="w-full border border-gray-200 rounded bg-white p-4">
        <h4 className="font-semibold text-primary mb-3">Review Refill Request</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-neutral-dark">
          {weightTrackingEnabled && (
            <p><span className="font-semibold">Current weight:</span> {formData.weight} lbs</p>
          )}
          <p><span className="font-semibold">Dose request:</span> {doseRequestLabels[formData.doseRequest]} ({selectedDose?.dosage})</p>
          <p><span className="font-semibold">Duration:</span> {formData.order_qty} month{formData.order_qty === '1' ? '' : 's'}</p>
          <p><span className="font-semibold">Quantity:</span> {formatDispenseQuantity(selectedDose, formData.order_qty) || 'Not available'}</p>
          <p><span className="font-semibold">Estimated price:</span> {formatCurrency(estimatedPrice)}</p>
          <p><span className="font-semibold">Abdominal pain:</span> {formData.abdominal_pain}</p>
          {formData.abdominal_pain === 'yes' && <p><span className="font-semibold">Abdominal pain details:</span> {formData.abdominal_pain_details}</p>}
          <p><span className="font-semibold">Nausea/vomiting/diarrhea:</span> {formData.gastrointestinal}</p>
          {formData.gastrointestinal === 'yes' && <p><span className="font-semibold">GI details:</span> {formData.gastrointestinal_details}</p>}
          <p><span className="font-semibold">Other side effects:</span> {formData.other_side_effects}</p>
          {formData.other_side_effects === 'yes' && <p><span className="font-semibold">Other side effect details:</span> {formData.other_side_effects_details}</p>}
          <p><span className="font-semibold">Additional notes:</span> {formData.complaint || 'None'}</p>
        </div>
        <p className="text-sm text-gray-600 mt-4">
          Submitting creates a refill order. Medication processing does not begin until payment is recorded.
        </p>
      </section>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setCurrentStep('request')} className="px-4 py-3 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
          Edit
        </button>
        <button type="button" onClick={handleSubmitRefill} disabled={isProcessing} className="px-4 py-3 bg-button text-white rounded hover:bg-button-light disabled:opacity-50">
          {isProcessing ? 'Submitting...' : 'Submit Refill Request'}
        </button>
      </div>
    </div>
  );

  const renderPayment = () => (
    <div className="space-y-4">
      {!showPaymentReceipt && (
        <section className="w-full border border-yellow-200 bg-yellow-50 text-yellow-900 rounded p-4 text-sm">
          Refill order {newOrderDetails?.order_id ? `#${newOrderDetails.order_id}` : ''} has been created and is waiting for payment. If you leave this page, return from your dashboard or orders list instead of submitting another refill request.
        </section>
      )}
      <PaymentForm
        user={user}
        order={newOrderDetails}
        showReceipt={showPaymentReceipt}
        receiptData={receiptData}
        onSuccess={handlePaymentSuccess}
        onCancel={handlePaymentError}
      />
    </div>
  );

  const renderStep = () => {
    if (isRefillDataLoading) {
      return (
        <div className="border border-blue-200 bg-blue-50 text-blue-900 rounded p-4">
          Loading medication history...
        </div>
      );
    }

    if (!lastOrder && !orderContext?.isLoading && !loadingSession && hasSettledRefillData) {
      return (
        <div className="border border-yellow-200 bg-yellow-50 text-yellow-900 rounded p-4">
          No previous qualifying medication order was found.
        </div>
      );
    }

    if (currentStep === 'review') return renderReview();
    if (currentStep === 'payment') return renderPayment();
    return renderRequest();
  };

  return (
    <div className="w-full flex flex-col flex-grow max-w-6xl justify-start mb-0 pb-0">
      <h3 className="text-2xl font-medium mb-4 text-primary text-center">
        Request a Refill
      </h3>

      {(isProcessing || orderContext?.isLoading || isRefillProductsLoading || loadingSession) && <Spinner overlay={true} />}
      {error && <ErrorDisplay message={error} className="my-4" />}
      {renderStep()}
    </div>
  );
}

export default RefillsPage;
